import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { storage } from '@/game-engine/storage';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { normalizeUsernameInput } from '@/utils/username';
import { migrateLocalDataToCloud } from '@/services/migration';
import { initLogRocket, identifyUser } from '@/lib/logrocket';
import { logger } from '@/utils/logger';
import type { User } from '@supabase/supabase-js';

export interface UserContextValue {
  /** Current username (null if not set) */
  username: string | null;
  /** Synchronously update username with validation and storage. Returns true on success. */
  updateUsername: (username: string) => boolean;
  /** Auth user object from Supabase (cloud users only) */
  user: User | null;
  /** Whether user is authenticated with cloud (has Supabase session) */
  isCloudUser: boolean;
  /** Whether user has a username set (local or cloud) */
  isAuthenticated: boolean;
  /** Whether user data is still loading from storage/cloud */
  isLoading: boolean;
  /** Synchronously set/update username with validation and storage. Returns true on success, false on invalid input. */
  login: (username: string) => boolean;
  /** Clear username and sign out from cloud if authenticated */
  logout: () => Promise<void>;
}

const UserContext = createContext<UserContextValue | null>(null);

function deriveUsernameFromUser(user: User | null): string | null {
  if (!user) return null;
  return (
    user.user_metadata?.username ||
    user.user_metadata?.display_name ||
    user.email?.split('@')?.[0] ||
    null
  );
}

export interface UserProviderProps {
  children: ReactNode;
}

/**
 * UserContext Provider
 * 
 * Provides user authentication and username management.
 */
export function UserProvider({ children }: UserProviderProps) {
  const { user: authUser, loading: authLoading, signOut } = useAuth();
  const [username, setUsernameState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    let cancelled = false;

    async function loadUser() {
      if (authUser) {
        let resolvedUsername = deriveUsernameFromUser(authUser);

        if (supabase) {
          const { data, error } = await supabase
            .from('user_profiles')
            .select('username')
            .eq('id', authUser.id)
            .maybeSingle();

          if (error) {
            (logger as any).error('Failed to fetch user profile:', error);
          } else if (data?.username) {
            resolvedUsername = data.username;
          }
        }

        if (!cancelled) {
          setUsernameState(resolvedUsername);
          setIsLoading(false);
        }

        if (resolvedUsername) {
          try {
            const result = await migrateLocalDataToCloud({
              userId: authUser.id,
              username: resolvedUsername,
            });

            if (result && !result.success && result.error) {
              (logger as any).error('Migration failed:', result.error);
            }
          } catch (error) {
            (logger as any).error('Migration failed:', error);
          }
        }

        return;
      }

      const storedUsername = (storage as any).loadUsername();
      if (!cancelled) {
        setUsernameState(storedUsername);
        setIsLoading(false);
      }
    }

    loadUser();

    return () => {
      cancelled = true;
    };
  }, [authLoading, authUser]);

  const login = (newUsername: string): boolean => {
    const normalizedUsername = normalizeUsernameInput(newUsername);
    if (!normalizedUsername) {
      return false;
    }

    setUsernameState(normalizedUsername);
    (storage as any).saveUsername(normalizedUsername);
    return true;
  };

  const logout = async (): Promise<void> => {
    if (authUser) {
      await signOut();
      setUsernameState(null);
      return;
    }

    setUsernameState(null);
    (storage as any).saveUsername(null);
  };

  useEffect(() => {
    initLogRocket().then(() => {
      if (username) {
        const userId = authUser?.id || username;
        identifyUser(userId, {
          username,
          isCloudUser: Boolean(authUser),
        });
      }
    });
  }, [username, authUser]);

  // Semantic alias for login - clearer intent when updating existing username
  const updateUsername = login;

  const value = useMemo<UserContextValue>(
    () => ({
      username,
      updateUsername,
      user: authUser,
      isCloudUser: Boolean(authUser),
      isAuthenticated: Boolean(username),
      isLoading: isLoading || authLoading,
      login,
      logout,
    }),
    [username, authUser, isLoading, authLoading]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextValue {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
}
